// The rules, the same ones the app shows in its dialog. They are not written here: they are
// generated out of app/src/main/res/values/strings.xml, so the two clients cannot end up teaching
// the same game two different ways.
//
// The app asks which game first and then opens one scrollable dialog. A browser has more room than
// a dialog, so the choice is a row of chips that stays on screen and the text scrolls under it -
// the same two steps, without having to go back to pick a different game.

import { el, clear } from './dom.js';
import { RULES } from '../generated/rules.js';

// Below this many characters in common, two names agree only by accident
const MinimumTitleAgreement = 4;

export class RulesView {
  #root;
  #chips;
  #body;
  #handlers;
  #chosen = 0;

  // handlers: { onBack }
  constructor(handlers) {
    this.#handlers = handlers;
    this.#chips = el('div', { className: 'rules-games' });
    this.#body = el('div', { className: 'panel rules-body' });
    this.#root = el('div', { className: 'screen' }, [
      el('h1', { className: 'title', text: 'Regole del gioco' }),
      el('p', { className: 'status', text: 'Di che gioco?' }),
      this.#chips,
      this.#body,
      el('button', {
        className: 'button button-soft',
        type: 'button',
        text: 'Torna indietro',
        onclick: () => this.#handlers.onBack(),
      }),
    ]);
    this.#render();
  }

  get element() {
    return this.#root;
  }

  // Which game to open on. The lobby knows which one is selected there, so arriving from it opens
  // the rules of the game about to be played rather than always the first in the list
  show(gameName) {
    const name = gameName ?? '';
    this.#chosen = Math.max(0, indexFor(name));
    this.#render();
  }

  #render() {
    clear(this.#chips);
    RULES.forEach((game, index) => {
      this.#chips.append(
        el('button', {
          className: index === this.#chosen ? 'chip selected' : 'chip',
          type: 'button',
          text: game.title,
          onclick: () => {
            this.#chosen = index;
            this.#render();
            // A different game means a different text, and reading it from where the last one was
            // scrolled to reads as the middle of a sentence
            this.#body.scrollTop = 0;
          },
        }),
      );
    });

    clear(this.#body);
    this.#body.append(el('h2', { text: RULES[this.#chosen].title }));
    for (const block of RULES[this.#chosen].blocks) {
      this.#body.append(blockNode(block));
    }
  }
}

// Which rules a game goes to. Several games share a text and so do not match its title outright:
// both Scoponi are explained under "Scopone e scopone scientifico", and each chiamata covers its
// two auctions. The longest title that agrees with the name for longest is the right one -
// "Briscola a 5 (asta a punti)" agrees with "Briscola a 5 (bastarda)" for thirteen characters and
// with plain "Briscola" for eight
function indexFor(name) {
  if (!name) return -1;
  const exact = RULES.findIndex((game) => game.title === name);
  if (exact >= 0) return exact;
  let best = -1;
  let longest = MinimumTitleAgreement;
  RULES.forEach((game, index) => {
    let shared = 0;
    while (shared < game.title.length && shared < name.length && game.title[shared] === name[shared]) {
      shared += 1;
    }
    if (shared > longest) {
      longest = shared;
      best = index;
    }
  });
  return best;
}

// Real elements out of the runs the generator produced: no string here is ever parsed as markup
function blockNode(block) {
  const runs = block.runs.map((run) =>
    run.strong ? el('strong', { text: run.text }) : document.createTextNode(run.text),
  );
  if (block.kind === 'heading') {
    return el('h3', { className: 'rules-heading' }, runs);
  }
  if (block.kind === 'bullet') {
    return el('p', { className: 'rules-bullet' }, runs);
  }
  return el('p', { className: 'rules-paragraph' }, runs);
}
